import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, Briefcase } from "lucide-react";
import { MicButton } from "@/components/MicButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "tacc_coach_session";

const EASE = [0.65, 0, 0.35, 1];
const TRIGGER_MOTION = {
  initial: { scale: 0, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  transition: { delay: 1.4, duration: 0.6, ease: EASE },
};
const PANEL_MOTION = {
  initial: { opacity: 0, y: 20, scale: 0.96 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 20, scale: 0.96 },
  transition: { duration: 0.35, ease: EASE },
};

const STARTERS = [
  "I'm walking into a hard shift.",
  "I can't sleep after today.",
  "One coworker is draining me.",
  "How do I ground in 60 seconds?",
];

const SESSION_RE = /^coach_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

function getOrCreateSession() {
  let s = sessionStorage.getItem(STORAGE_KEY);
  if (!s || !SESSION_RE.test(s)) {
    s = "coach_" + crypto.randomUUID();
    sessionStorage.setItem(STORAGE_KEY, s);
  }
  return s;
}

export default function CoachChat() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {role, content}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [proposalLoading, setProposalLoading] = useState(false);
  const [sessionId] = useState(getOrCreateSession);
  const scrollRef = useRef(null);

  const userMsgCount = useMemo(
    () => messages.filter((m) => m.role === "user").length,
    [messages]
  );
  const showProposalCTA = userMsgCount >= 3 && !busy;

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/coach/history/${sessionId}`)
      .then((r) => r.json())
      .then((d) => setMessages(d.messages || []))
      .catch((e) => console.warn("Coach history unavailable:", e));
  }, [open, sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/coach/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "The coach is unreachable." }));
        setMessages((m) => {
          const copy = [...m]; copy[copy.length - 1] = { role: "assistant", content: err.detail || "Try again shortly." }; return copy;
        });
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m]; copy[copy.length - 1] = { role: "assistant", content: acc }; return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m]; copy[copy.length - 1] = { role: "assistant", content: "Connection lost. Try again." }; return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  const requestProposal = async () => {
    if (proposalLoading) return;
    setProposalLoading(true);
    try {
      const res = await fetch(`${API}/coach/proposal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Could not draft a proposal." }));
        setMessages((m) => [...m, { role: "assistant", content: err.detail || "Chat a little more, then try again." }]);
        return;
      }
      const data = await res.json();
      const handoff = {
        summary: data.summary || "",
        suggested_tier: data.suggested_tier || "team",
        created_at: Date.now(),
      };
      sessionStorage.setItem("tacc_coach_handoff", JSON.stringify(handoff));
      setOpen(false);
      navigate("/b2b?from=coach#quote");
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Connection lost while drafting your proposal. Try again in a moment." }]);
    } finally {
      setProposalLoading(false);
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <motion.button
        {...TRIGGER_MOTION}
        onClick={() => setOpen((o) => !o)}
        data-testid="coach-toggle"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#F59E0B] text-black flex items-center justify-center shadow-[0_20px_40px_-12px_rgba(245,158,11,0.5)] hover:scale-[1.06] transition-transform"
        aria-label={open ? "Close coach" : "Ask T.A.C.C."}
      >
        {open ? <X size={20} strokeWidth={2} /> : <MessageCircle size={22} strokeWidth={1.8} />}
      </motion.button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            {...PANEL_MOTION}
            data-testid="coach-panel"
            className="fixed bottom-24 right-6 z-40 w-[min(420px,calc(100vw-3rem))] h-[min(600px,calc(100vh-8rem))] bg-[#FFFFFF] border border-[#E7DFD5] shadow-2xl flex flex-col overflow-hidden"
          >
            <header className="px-5 py-4 border-b border-[#E7DFD5] flex items-center justify-between">
              <div>
                <div className="section-tag">Ask T.A.C.C.</div>
                <div className="font-heading text-sm mt-0.5">Your healing coach</div>
              </div>
              <span className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#78716C]">AI · Claude</span>
            </header>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
              {messages.length === 0 && (
                <div className="space-y-4">
                  <p className="font-display text-2xl leading-tight tracking-tight">
                    Take a breath.<br /><span className="italic text-[#B45309]">Then talk to me.</span>
                  </p>
                  <p className="text-[#78716C] text-sm leading-relaxed">
                    I speak in the voice of the doctrine. Stoic, direct, actionable. Start with anything below or type your own.
                  </p>
                  <div className="flex flex-col gap-2 pt-2">
                    {STARTERS.map((s) => (
                      <button
                        key={s}
                        onClick={() => send(s)}
                        data-testid="coach-starter"
                        disabled={busy}
                        className="text-left px-4 py-2.5 border border-[#E7DFD5] text-sm text-[#44403C] hover:border-[#F59E0B] hover:text-[#B45309] transition-colors disabled:opacity-50"
                      >
                        “{s}”
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={`${m.role}-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`coach-msg-${m.role}`}
                    className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[#F59E0B] text-black"
                        : "bg-[#F5EFEB] border border-[#E7DFD5] text-[#44403C]"
                    }`}
                  >
                    {m.content || (busy && i === messages.length - 1 ? "…" : "")}
                  </div>
                </div>
              ))}
              {busy && messages.length > 0 && messages[messages.length - 1].role === "assistant" && !messages[messages.length - 1].content && (
                <Loader2 size={16} className="text-[#B45309] animate-spin" />
              )}
            </div>

            <div className="p-4 border-t border-[#E7DFD5]">
              {showProposalCTA && (
                <button
                  onClick={requestProposal}
                  disabled={proposalLoading}
                  data-testid="coach-proposal-cta"
                  className="w-full mb-3 flex items-center justify-between gap-2 px-4 py-3 border border-[#F59E0B]/40 bg-[#F59E0B]/5 hover:bg-[#F59E0B]/10 transition-colors text-left group"
                >
                  <div className="flex items-center gap-3">
                    <Briefcase size={16} className="text-[#B45309]" />
                    <div>
                      <div className="text-xs font-medium text-[#1C1917] leading-tight">This sounds bigger than one shift.</div>
                      <div className="text-[10px] text-[#78716C] mt-0.5">Draft a B2B proposal for your team →</div>
                    </div>
                  </div>
                  {proposalLoading ? (
                    <Loader2 size={14} className="text-[#B45309] animate-spin shrink-0" />
                  ) : (
                    <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#B45309] shrink-0">Get proposal</span>
                  )}
                </button>
              )}
              <div className="flex items-end gap-2">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  rows={1}
                  placeholder="Say what's heavy right now…"
                  data-testid="coach-input"
                  className="flex-1 bg-[#FFFFFF] border border-[#E7DFD5] px-3 py-2.5 text-sm text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:border-[#F59E0B] resize-none max-h-32"
                />
                <MicButton
                  testId="coach-mic"
                  context="Someone talking to a workplace healing coach about a hard shift."
                  onText={(t) => setInput((v) => (v ? `${v} ${t}` : t))}
                  disabled={busy}
                />
                <button
                  onClick={() => send()}
                  disabled={busy || !input.trim()}
                  data-testid="coach-send"
                  className="w-11 h-11 bg-[#F59E0B] text-black flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#D97706] transition-colors"
                  aria-label="Send"
                >
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
              </div>
              <p className="mt-2 font-mono text-[9px] tracking-[0.2em] uppercase text-[#A8A29E]">
                Not a replacement for medical care. Crisis? Call or text 988.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
